import React, { useState } from 'react';
import { CultureItem, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { BookOpen, Share2, Printer, ChevronLeft, ChevronRight, X, ArrowRight } from 'lucide-react';

interface CultureViewProps {
  currentLang: LanguageCode;
  cultureItems: CultureItem[];
}

export const CultureView: React.FC<CultureViewProps> = ({
  currentLang,
  cultureItems,
}) => {
  const t = translations[currentLang];
  const [activeTab, setActiveTab] = useState<string>('Tüm');
  const [selectedCulture, setSelectedCulture] = useState<CultureItem | null>(null);
  const [shareToast, setShareToast] = useState(false);

  const handleShare = (titleText?: string) => {
    if (navigator.share) {
      navigator.share({
        title: titleText || t.cultureBadge,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 3000);
    }
  };

  const rawCategories = ['Tüm', 'Tarih', 'Dil', 'Müzik', 'Edebiyat', 'Kıyafetler', 'Yemekler'];

  const filtered = cultureItems.filter(
    (item) => activeTab === 'Tüm' || item.category === activeTab
  );

  // Vocabulary list for interactive Kurmancî language section
  const vocabList = [
    { km: 'Rojbaş', tr: 'Günaydın / İyi günler', pron: 'Roj-bash' },
    { km: 'Spas dikim', tr: 'Teşekkür ederim', pron: 'Spas di-kim' },
    { km: 'Tu çawa yî?', tr: 'Nasılsın?', pron: 'Tu cha-wa yi?' },
    { km: 'Ez baş im', tr: 'İyiyim', pron: 'Ez bash im' },
    { km: 'Bi xêr hatî', tr: 'Hoş geldin', pron: 'Bi kher ha-ti' },
    { km: 'Jiyan nû ye', tr: 'Hayat yenidir', pron: 'Ji-yan nu ye' },
  ];

  // Next & Prev Culture item navigation
  const currentIndex = selectedCulture ? filtered.findIndex((c) => c.id === selectedCulture.id) : -1;
  const handlePrevCulture = () => {
    if (currentIndex > 0) {
      setSelectedCulture(filtered[currentIndex - 1]);
    }
  };
  const handleNextCulture = () => {
    if (currentIndex >= 0 && currentIndex < filtered.length - 1) {
      setSelectedCulture(filtered[currentIndex + 1]);
    }
  };

  return (
    <div className="space-y-10 pb-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
          {t.cultureBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.kurdishCulture}
        </h1>
        <p className="text-sm text-gray-300">
          {t.cultureDesc}
        </p>
      </div>

      {/* Category Pills */}
      <div className="flex items-center justify-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        {rawCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveTab(cat)}
            className={`px-4 py-2 rounded-2xl text-xs font-bold whitespace-nowrap transition-all ${
              activeTab === cat
                ? 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_15px_rgba(56,189,248,0.6)]'
                : 'bg-[#0B1426] text-gray-300 border border-[#38BDF8]/20 hover:bg-[#132238]'
            }`}
          >
            {getLocalizedCategory(cat, currentLang)}
          </button>
        ))}
      </div>

      {/* Interactive Kurdish Language Section if Dil or Tüm */}
      {(activeTab === 'Dil' || activeTab === 'Tüm') && (
        <section className="glass-card rounded-3xl p-6 sm:p-8 border border-[#38BDF8]/40 bg-[#132238] space-y-6">
          <div className="flex items-center justify-between border-b border-[#38BDF8]/20 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white font-heading">
                  {t.interactiveLangGuide}
                </h2>
                <p className="text-xs text-gray-300">
                  {t.usefulPhrases}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {vocabList.map((item, idx) => (
              <div
                key={idx}
                className="p-4 rounded-2xl bg-[#0B1426] border border-[#38BDF8]/20 hover:border-[#38BDF8] transition-all group"
              >
                <p className="text-sm font-bold text-[#38BDF8] group-hover:text-white transition-colors">
                  {item.km}
                </p>
                <p className="text-xs text-gray-200 mt-0.5">{item.tr}</p>
                <p className="text-[10px] text-gray-400 font-mono mt-1">[{item.pron}]</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Culture Items Showcase Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="glass-card rounded-3xl overflow-hidden border border-[#38BDF8]/30 hover:border-[#38BDF8] hover:shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-all duration-300 flex flex-col justify-between group"
          >
            <div className="relative h-64 overflow-hidden">
              <img
                src={item.image}
                alt={getLocalized(item.title, currentLang)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#0B1426]/90 backdrop-blur-md text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/40 shadow-[0_0_8px_rgba(56,189,248,0.4)]">
                {getLocalizedCategory(item.category, currentLang)}
              </span>
            </div>

            <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-bold text-white font-heading group-hover:text-[#38BDF8] transition-colors">
                  {getLocalized(item.title, currentLang)}
                </h3>
                {item.subtitle && (
                  <p className="text-xs text-[#38BDF8] font-medium mt-1">
                    {getLocalized(item.subtitle, currentLang)}
                  </p>
                )}
                <p className="text-xs sm:text-sm text-gray-300 mt-3 leading-relaxed line-clamp-3">
                  {getLocalized(item.description, currentLang)}
                </p>
              </div>

              <div className="pt-2 flex items-center justify-between border-t border-[#38BDF8]/15">
                <button
                  onClick={() => handleShare(getLocalized(item.title, currentLang))}
                  className="px-3 py-1.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-gray-300 hover:text-[#38BDF8] text-xs font-medium flex items-center gap-1.5"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Paylaş</span>
                </button>

                <button
                  onClick={() => setSelectedCulture(item)}
                  className="px-4 py-2 rounded-xl bg-[#132238] border border-[#38BDF8] text-[#38BDF8] text-xs font-bold hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all flex items-center gap-1.5"
                >
                  <span>{t.details || 'Detaylar'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Share Toast Banner */}
      {shareToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#38BDF8] text-[#0B1426] px-5 py-3 rounded-2xl font-extrabold text-xs shadow-[0_0_20px_rgba(56,189,248,0.8)] flex items-center gap-2 border border-white animate-bounce">
          <Share2 className="w-4 h-4 stroke-[3]" />
          <span>Bağlantı kopyalandı!</span>
        </div>
      )}

      {/* Culture Detail Overlay (Clean, Flat, Fixed Header with Geri/İleri/Kapat & Oval Glowing Image) */}
      {selectedCulture && (
        <div className="fixed inset-0 z-50 bg-[#0B1426] text-white flex flex-col overflow-hidden animate-in fade-in duration-200">
          {/* Fixed Top Header (En Üstte Sabit) */}
          <div className="sticky top-0 z-30 bg-[#0B1426]/95 backdrop-blur-md px-3 sm:px-6 py-3 border-b border-[#38BDF8]/40 shadow-[0_4px_20px_rgba(56,189,248,0.25)] flex items-center justify-between">
            {/* Left: Geri / İleri */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevCulture}
                disabled={currentIndex <= 0}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex <= 0
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Geri</span>
              </button>

              <button
                onClick={handleNextCulture}
                disabled={currentIndex >= filtered.length - 1}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex >= filtered.length - 1
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <span className="hidden sm:inline">İleri</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Center: Category Badge */}
            <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/50 shadow-[0_0_10px_rgba(56,189,248,0.4)] truncate max-w-[150px] sm:max-w-xs">
              {getLocalizedCategory(selectedCulture.category, currentLang)}
            </span>

            {/* Right: Kapat (Close) */}
            <button
              onClick={() => setSelectedCulture(null)}
              className="px-3.5 py-1.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all flex items-center gap-1"
            >
              <span>{t.close || 'Kapat'}</span>
              <X className="w-4 h-4 stroke-[3]" />
            </button>
          </div>

          {/* Scrollable Content Body (Düz, Çerçevesiz, Sağda ve Solda 0.5 Boşluk) */}
          <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-6 max-w-4xl mx-auto w-full space-y-6 scrollbar-thin">
            {/* Oval Glowing Image Frame */}
            {selectedCulture.image && (
              <div className="relative w-full max-h-[380px] rounded-[2.5rem] overflow-hidden border-2 border-[#38BDF8] shadow-[0_0_25px_rgba(56,189,248,0.5)]">
                <img
                  src={selectedCulture.image}
                  alt="Culture Detail"
                  className="w-full h-full object-cover max-h-[380px]"
                />
              </div>
            )}

            {/* Title & Subtitle */}
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading leading-tight">
                {getLocalized(selectedCulture.title, currentLang)}
              </h2>
              {selectedCulture.subtitle && (
                <p className="text-sm font-semibold text-[#38BDF8]">
                  {getLocalized(selectedCulture.subtitle, currentLang)}
                </p>
              )}
            </div>

            {/* Content Text (Düz, Çerçevesiz) */}
            <div className="text-sm sm:text-base text-gray-200 leading-relaxed whitespace-pre-line py-2">
              {getLocalized(selectedCulture.description, currentLang)}
            </div>

            <div className="text-sm sm:text-base text-gray-200 leading-relaxed whitespace-pre-line py-2 border-t border-[#38BDF8]/20 pt-4">
              {getLocalized(selectedCulture.content, currentLang)}
            </div>

            {/* Bottom Actions: Paylaş & Yazdır */}
            <div className="pt-8 pb-12 border-t border-[#38BDF8]/20 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleShare(getLocalized(selectedCulture.title, currentLang))}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8] text-[#38BDF8] font-extrabold text-xs hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all shadow-[0_0_12px_rgba(56,189,248,0.4)] flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Paylaş</span>
                </button>

                <button
                  onClick={() => window.print()}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8]/50 text-gray-200 font-bold text-xs hover:bg-[#1C3352] hover:text-white transition-all flex items-center gap-2"
                >
                  <Printer className="w-4 h-4 text-[#38BDF8]" />
                  <span>Yazdır</span>
                </button>
              </div>

              <button
                onClick={() => setSelectedCulture(null)}
                className="px-6 py-2.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all"
              >
                {t.close || 'Kapat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
